import { useEffect, useRef } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  ahuBoardApi,
  refetchSectionsAfterTunnelResize,
  refetchTunnelsAfterAddressingChange,
  clearOwnTunnelWrites,
  consumeOwnTunnelWrite,
  refetchTunnelsAfterBaseChange,
  isLocalBaseWriteInFlight,
  refetchTunnelsAfterBaseSideChange,
  refetchTunnelsAfterFootprintChange,
} from 'app/services/ahuBoardApi';
import { ahuComponentFormsApi } from 'app/services/ahuComponentFormsApi';
import { store } from 'app/store';
import { getSocket, type SocketDataMessage } from 'app/socketClient';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { getRegisteredModel } from '../forms/autoSaveModel';
import { hasPendingSectionPatch } from '../forms/sectionPatchQueue';
import { recalcTunnelLength } from '../tunnelLength';
import {
  followSectionLengthChange,
  refetchLugPlacementsAfterGeometryChange,
} from '../boardActions';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';

/**
 * AHU-board syncdata listener — the React port of Backbone's
 * `django/ahu/static/ahu/mvc/fn/syncdata_listener.js`, covering three of its
 * branches: `onPut` for the model that drives the 3D canvas (AHUSection) and
 * for AHUCoil (see `onPutCoil`), plus `onPostAccessories` (its own note
 * further down).
 *
 * WHY THIS EXISTS — a component SELECT does not return the new geometry.
 * Selecting a fan/coil/wheel/… posts to `…/<id>/select`; the backend then
 * recomputes the host section's `length` / `aw_length` from the chosen
 * component and saves the AHUSection. That save is never echoed in the select
 * response, so the ONLY channel carrying it back to the browser is the
 * syncdata websocket: `django/syncdata/signals.py:329` `signal_post_save` →
 * AMQP `syncdata` queue → the node/socket.io server → a `'data'` frame
 * (`{type:'syncdata', code:'put', model:'ahusection', data:<serialized row>}`).
 *
 * Backbone consumes it in `syncdata_listener.js:onPut`
 * (`instance.set(message.data, {__from_back_end:true})`), and the tunnel model
 * reacts (`model/tunnel.js:118-129`): when BOTH `length` and `aw_length` land
 * in `changed` it fires `section:length:changed`,
 * `change:tunnel:dimensions {render:true}` and `tunnelLugs:recalcPlacement`,
 * which redraws the tunnel. React had no section syncdata listener at all
 * (`boardActions.ts:249-252` notes the same gap for section inserts), so the
 * canvas kept its pre-select geometry until a full page reload.
 *
 * TWO DELIBERATE NARROWINGS vs the Backbone listener, both documented here so
 * the next reader does not "restore" them into bugs:
 *
 * 1. The redraw is left to the existing React data path rather than re-emitting
 *    `change:tunnel:dimensions`. Writing the row into the `getTunnelSectionList`
 *    cache changes `sectionsByTunnel` → `canvasTunnels` identity
 *    (`AhuBoardPage.tsx:259`), and `AhuCanvas.tsx:922-937` turns a new tunnels
 *    identity into `redrawCanvasDependsOnAHU()` — the same camera-preserving
 *    render `change:tunnel:dimensions {render:true}` reaches, and the pattern
 *    `boardActions.ts:249` already uses ("the cache write cascades into the
 *    canvas props (same observable re-render)"). Emitting the bus event as well
 *    would render the tunnel twice for one message. The two SIBLING events
 *    legacy fires on the same condition — `section:length:changed` and
 *    `tunnelLugs:recalcPlacement` — have no such cascade and ARE emitted.
 *
 * 2. Only fields that differ are merged, and never while the user's own PATCH
 *    for that section is still in flight. Backbone sets the whole model
 *    unconditionally; here an unconditional `updateQueryData` would mint a new
 *    array identity for every echo (including the echo of the user's own edit)
 *    and redraw the canvas on each keystroke-driven save, and could briefly
 *    revert a field the user is still editing. Both are avoided by diffing
 *    first and deferring to the in-flight patch.
 *
 * The section row is NOT the only thing the canvas needs: it draws the cabinet
 * from `tunnel.length` and the section bands from the section rows, so both
 * caches have to move together or the redraw is internally inconsistent. Legacy
 * keeps them in lockstep client-side with `tunnel.js:141-150` `recalc_length`,
 * ported here as `recalcTunnelLength` — see its call site.
 */

/** Django `content_type.model` for AHUSection (signals.py sends it lowercased). */
const SECTION_DJANGO_MODEL = 'ahusection';

/** …and for AHUCoil, `models.mapping.js:30` → `component.coil.model`. */
const COIL_DJANGO_MODEL = 'ahucoil';

/** …and for AHUFilter, `models.mapping.js:32` → `component.filter.model`. */
const FILTER_DJANGO_MODEL = 'ahufilter';

/** …and for AHUTunnels, `models.mapping.js` → `tunnel.model`. */
const TUNNEL_DJANGO_MODEL = 'ahutunnels';

/**
 * `syncdata_listener.js:190-197` — the master-coil fields whose change means
 * the coil's RATING was invalidated behind the user's back (the sizer's APPLY
 * is the only writer that does it from another tab). Verbatim, including the
 * four names `AHUCoilRatingModeSerializer` does not carry: legacy compares
 * `master_coil` dicts, so `qty_stacked` / `face_velocity_actual` / `duty_id` /
 * `airflow_actual` are `undefined` on both sides and never trip the probe —
 * leaving `model` / `fh` / `fl`, which is exactly what an APPLY moves (and
 * `update_coil` NULLs `model` whenever it moves anything at all).
 */
const COIL_SIZER_APPLY_FIELDS = [
  'model',
  'fh',
  'fl',
  'qty_stacked',
  'face_velocity_actual',
  'duty_id',
  'airflow_actual',
] as const;

/** `refetchCoil`'s own debounce (components/coil/view/form.js:302-309). */
const COIL_REFETCH_DEBOUNCE_MS = 200;

/**
 * `content_type.model` for every CONCRETE accessory table — the React
 * equivalent of the `models.mapping.js:42-57` entries whose mapped name starts
 * `model.accessory.`, which is exactly the set `syncdata_listener.js:78-79`
 * routes to `onPostAccessories`.
 *
 * `ahuaccessory` (mapped `accessory.model`) is deliberately absent: it is the
 * shared sequence row, it carries no `section_id`, and legacy does not route it
 * here either.
 */
const ACCESSORY_DJANGO_MODELS = new Set([
  'ahuaccessoryopening',
  'ahuaccessoryaccessdoor',
  'ahuaccessorylouver',
  'ahuaccessorydamper',
  'ahuaccessorydrainpan',
  'ahuaccessorylight',
  'ahuaccessoryenclosure',
  'ahuaccessoryadpgage',
  'ahuaccessoryvfd',
  'ahuaccessoryjbox',
  'ahuaccessoryhood',
  'ahuaccessorystarter',
  'ahuaccessorydisconnect',
  'ahuaccessoryuvc',
  'ahuuserdefinedaccessory',
  'ahuaccessorydoghouse',
]);

/** Legacy `onPostAccessories` coalescing window (syncdata_listener.js:171). */
const ACCESSORY_POST_DEBOUNCE_MS = 500;

/**
 * Legacy `tunnel.js:121` gate: the tunnel re-renders only when BOTH `length`
 * and `aw_length` are in Backbone's `changed` map — i.e. both differ from what
 * the client already holds.
 */
const DIMENSION_FIELDS = ['length', 'aw_length'] as const;

export function useAhuBoardSyncdata(
  ahuId: number | null,
  tunnelIds: number[] | null
): void {
  const dispatch = useAppDispatch();
  // Read the live tunnel set from a ref so the listener is attached once per
  // board mount rather than re-bound on every bootstrap refetch.
  const tunnelIdsRef = useRef<number[] | null>(tunnelIds);
  tunnelIdsRef.current = tunnelIds;
  const ahuIdRef = useRef<number | null>(ahuId);
  ahuIdRef.current = ahuId;

  // Claims are a per-board thing: leaving the board drops any that a save in
  // flight never got to consume, so the next board cannot inherit one and
  // skip a re-read it needs.
  useEffect(() => () => clearOwnTunnelWrites(), []);

  useEffect(() => {
    const socket = getSocket();
    /** Per-section coalescing timers for the accessory `post` branch. */
    const accessoryTimers = new Map<number, number>();
    /** Per-coil `refetchCoil` timers (the coil `put` branch). */
    const coilTimers = new Map<number, number>();

    /**
     * Legacy's `stateStorage.models.get('section.model', section_id)` guard
     * (syncdata_listener.js:148-150 / :133-135): a message about a section this
     * board does not hold has no instance to touch, so it is dropped.
     */
    const sectionOnThisBoard = (sectionId: number): boolean =>
      (tunnelIdsRef.current ?? []).some((tid) =>
        ahuBoardApi.endpoints.getTunnelSectionList
          .select(tid)(store.getState())
          .data?.some((row) => row.id === sectionId)
      );

    /**
     * `onPut` narrowed to `component.coil.model` — syncdata_listener.js:176-213.
     *
     * WHY THIS EXISTS — the sizer's APPLY (`sections/<id>/sizer_apply/`,
     * `section_viewset.py:690`) is a write to THIS board performed from the
     * `/sizer/` tab: it saves the coil's rating-mode row (FH/FL, casing
     * flanges), wipes the rating when the geometry moved (`update_coil`), and
     * saves the AHUCoil itself (airflow, qty stacked, duty). None of that comes
     * back through a request this tab made, so the websocket is the only
     * channel that carries it — exactly the situation the section branch above
     * was written for.
     *
     * Backbone does two things with the frame and both are ported:
     *  - `instance.set(message.data, {__from_back_end:true})` (:212) — the coil
     *    model backing the DATA BAR is updated in place, no save (§19.2 echo
     *    guard). `getRegisteredModel` is the React `stateStorage.models.get`.
     *  - the `sizer_apply` probe (:186-206) — when the master-coil fields below
     *    differ it calls `view.refetchCoil()`, which after 200 ms fires
     *    `update_rating_dialog:<coil_id>` and re-seeds an OPEN rating dialog
     *    (coil/view/form.js:302-309).
     *
     * ★ THE EMIT IS OURS AND IT COMES LAST. Round 1 leaned on `CoilForm`'s own
     * emitter (it fires the same event off a `getRatedCoil` diff), which left
     * the dialog stale on dev3 while the data bar updated. Two reasons not to
     * chain through it: the dialog seeds from `getComponentDetail` for `coils`
     * AND `rating_mode_coils` — neither of which that path refreshes — and its
     * emit is one indirection further from the frame that caused it. So the
     * three rows the two surfaces read are refetched and AWAITED here, and only
     * then is the event emitted, which means the re-seed cannot read a row that
     * is still pre-APPLY. `CoilForm`'s emitter then fires too; the second
     * re-seed is a no-op (`seeded.current === modelId` again by then).
     *
     * Without this the board kept its pre-APPLY coil until a full page reload,
     * in both the rating dialog and the data bar.
     */
    const onPutCoil = (msg: SocketDataMessage) => {
      const row = msg.data as Record<string, unknown> | undefined;
      const coilId = Number(row?.id ?? msg.id);
      const sectionId = Number(row?.section_id);
      if (!row || !Number.isFinite(coilId) || !Number.isFinite(sectionId)) return;
      if (!sectionOnThisBoard(sectionId)) return;

      // The probe runs BEFORE the set, like legacy's — it compares the incoming
      // row against what the client still holds. The form model is the primary
      // source (it is what legacy reads); the query cache covers a coil whose
      // form is not currently mounted.
      const model = getRegisteredModel('component.coil.model', coilId);
      const cached = ahuComponentFormsApi.endpoints.getComponentDetail.select({
        plural: 'coils',
        id: coilId,
      })(store.getState()).data;
      const oldMaster = (model
        ? model.get('master_coil')
        : cached?.master_coil) as Record<string, unknown> | null | undefined;
      const newMaster = row.master_coil as Record<string, unknown> | null | undefined;
      const ratingChanged =
        !!oldMaster &&
        !!newMaster &&
        COIL_SIZER_APPLY_FIELDS.some((field) => oldMaster[field] !== newMaster[field]);

      // The caches a form / the canvas reads on its NEXT mount. `getRatedCoil`
      // and the two tagged board queries are re-read below only when the rating
      // actually moved, so a plain PATCH echo costs no requests.
      dispatch(
        ahuComponentFormsApi.util.updateQueryData(
          'getComponentDetail',
          { plural: 'coils', id: coilId },
          (draft) => {
            Object.assign(draft, row);
          }
        )
      );

      // …and the live data bar — unless the user's own PATCH for this coil is
      // still on the wire, whose success handler writes the authoritative row
      // (narrowing 2 above, applied to `pennding` [sic] rather than the section
      // queue). Backbone sets unconditionally and its own comment warns about
      // it ("can lead to loops, so be aware!!!", :212).
      if (!model?.pennding) model?.set(row, { __from_back_end: true });

      if (!ratingChanged) return;
      window.clearTimeout(coilTimers.get(coilId));
      coilTimers.set(
        coilId,
        window.setTimeout(() => {
          coilTimers.delete(coilId);
          dispatch(
            ahuBoardApi.util.invalidateTags([
              { type: 'AhuComponents' as const, id: `coil-${coilId}` },
              { type: 'AhuComponents' as const, id: `rated-coil-${coilId}` },
            ])
          );
          // The three rows the dialog and the coil form seed from. Subscribed
          // so the entry is not evicted mid-flight; released once landed.
          const jobs = [
            dispatch(
              ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
                { plural: 'coils', id: coilId },
                { forceRefetch: true }
              )
            ),
            dispatch(
              ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
                { plural: 'rating_mode_coils', id: coilId },
                { forceRefetch: true }
              )
            ),
            dispatch(
              ahuComponentFormsApi.endpoints.getRatedCoil.initiate(coilId, {
                forceRefetch: true,
              })
            ),
          ];
          void Promise.all(jobs)
            .catch(() => undefined)
            .then(() => {
              jobs.forEach((req) => req.unsubscribe());
              // `refetchCoil` (coil/view/form.js:306) — re-seed the OPEN dialog.
              ahuBoardBus.emit(
                `update_rating_dialog:${coilId}` as `update_rating_dialog:${number}`
              );
            });
        }, COIL_REFETCH_DEBOUNCE_MS)
      );
    };

    /**
     * `onPut` for `component.filter.model` — the generic branch of
     * syncdata_listener.js:204, which sets ANY registered model from the frame.
     *
     * WHY THIS EXISTS — the sizer's APPLY writes the filter through the same
     * `sections/<id>/sizer_apply/` endpoint the coil branch above uses
     * (`section_viewset.py:743-767`): it moves `size_qty_id`,
     * `allowable_filter_sizes`, `max_different_sizes`,
     * `min_face_velocity` / `max_face_velocity`, forces `selection_condition`
     * to `show_all_configurations_that_fit`, drops `master_filter` /
     * `master_frame` when the saved filter is not in the new bank
     * configuration, and sets `airflow_actual`. Every one of those is written
     * from the `/sizer/` tab, so — exactly as for the coil — nothing this tab
     * requested carries it back and the websocket is the only channel.
     *
     * Legacy needs no filter-specific branch here because its `onPut` is
     * generic: `ahufilter` is in `models.mapping.js`, so `instance.set(…,
     * {__from_back_end:true})` lands on the filter model and BOTH surfaces
     * follow it — the DATA BAR (its form is bound to the model, and
     * `components/filter/view/form.js:183-193` re-runs the master_filter
     * catalogue filter off `change:size_qty_id`) and the OPEN SELECTION DIALOG
     * (`filter.selector.js:249-289` listens for the same `__from_back_end`
     * change and re-seeds itself). React's listener was narrowed to `ahucoil`,
     * so the filter model never moved and both surfaces held their pre-APPLY
     * values until a full page reload.
     *
     * No rating-refetch branch, unlike the coil: legacy has no `refetchFilter`
     * and the filter form reads only `getComponentDetail` for `filters` (there
     * is no `rating_mode_filters` / `getRatedFilter` row), which is written
     * here. `FilterForm`'s cross-filter web and `FilterSelector`'s re-seed both
     * hang off the model `change` event, so the `set` below is the trigger for
     * everything downstream.
     */
    const onPutFilter = (msg: SocketDataMessage) => {
      const row = msg.data as Record<string, unknown> | undefined;
      const filterId = Number(row?.id ?? msg.id);
      const sectionId = Number(row?.section_id);
      if (!row || !Number.isFinite(filterId) || !Number.isFinite(sectionId)) return;
      if (!sectionOnThisBoard(sectionId)) return;

      // The cache a filter form reads on its NEXT mount — and, because the
      // selection dialog seeds from the live model rather than a staging clone
      // (§33.7, THE shim #8 cut), the row a dialog opened AFTER the apply sees.
      dispatch(
        ahuComponentFormsApi.util.updateQueryData(
          'getComponentDetail',
          { plural: 'filters', id: filterId },
          (draft) => {
            Object.assign(draft, row);
          }
        )
      );

      // …and the live model, unless the user's own PATCH for this filter is
      // still on the wire (same narrowing as the coil branch).
      const model = getRegisteredModel('component.filter.model', filterId);
      if (!model?.pennding) model?.set(row, { __from_back_end: true });
    };

    /**
     * Port of `syncdata_listener.js:145-174` `onPostAccessories`.
     *
     * WHY THIS EXISTS — some accessories are created by the BACKEND, with no
     * client request to hang a refetch off. Selecting a cooling coil is the
     * case that surfaced it: `AHUCoil.save()` auto-creates the section's drain
     * pan (`models_component_coil.py:1611-1630`) as a side effect of the coil
     * PATCH, so the only channel that carries the new row to the browser is the
     * syncdata websocket `{code:'post', model:'ahuaccessorydrainpan'}` frame
     * (`syncdata/signals.py:361-372`; AHUAccessoryDrainPan is in
     * `ahu_models_listened`). Backbone adds the row to the section's accessory
     * collection here; React had NO `post` handling at all, so the drain pan
     * only appeared after a full page reload — and because the
     * "adding drain pan…" note is cleared by the drain-pan form's first render
     * (`accessory_view.js:833-838`, ported in `useAccessoryForm`), the note
     * stayed up forever too.
     *
     * Backbone splices the socket payload straight into the collection; here a
     * forced refetch of `getSectionAccessories` is both simpler and closer to
     * what the rest of the board does after creating an accessory
     * (`boardActions.ts:422-426`), and it keeps the row shape authoritative
     * rather than reconstructing legacy's `type`/`name` patch-up. Everything
     * downstream rides that cache write: `TabAccessories` re-renders the list,
     * which mounts the new row's form, which draws it on the canvas.
     */
    const onPostAccessory = (msg: SocketDataMessage) => {
      const sectionId = Number((msg.data as { section_id?: unknown } | undefined)?.section_id);
      const accessoryId = Number((msg.data as { id?: unknown } | undefined)?.id ?? msg.id);
      if (!Number.isFinite(sectionId)) return;

      // Scope: this board only.
      if (!sectionOnThisBoard(sectionId)) return;

      // `if (!accessories.get(message.id))` (:156) — the user's own creates are
      // echoed back to them, and the row is already in the cache by then.
      const known = ahuBoardApi.endpoints.getSectionAccessories
        .select(sectionId)(store.getState())
        .data?.some((row) => row.id === accessoryId);
      if (known) return;

      window.clearTimeout(accessoryTimers.get(sectionId));
      accessoryTimers.set(
        sectionId,
        window.setTimeout(() => {
          accessoryTimers.delete(sectionId);
          const req = dispatch(
            ahuBoardApi.endpoints.getSectionAccessories.initiate(sectionId, {
              forceRefetch: true,
            })
          );
          // Socket-driven, so unlike the component call sites there is no
          // component lifetime to own the subscription — release it once the
          // refetch has landed in the cache.
          void req.finally(() => req.unsubscribe());
        }, ACCESSORY_POST_DEBOUNCE_MS)
      );
    };

    /**
     * `onPut` for `tunnel.model` — the OTHER half of a sizer APPLY, and the one
     * that moves the cabinet.
     *
     * WHY THIS EXISTS — `apply()` posts `ahu/tunnels/<id>/sizer_apply/` for every
     * ticked tunnel (`sizer_popup.js:847-858`), which saves the tunnel's new
     * `width`/`height` and pushes `casing_insulation_id` down onto its sections
     * (`tunnel_viewset.py:334-346`). It is a write to THIS board performed from
     * the `/sizer/` tab, so — exactly like the coil and filter branches above —
     * the websocket is the only channel that carries it back, and React handled
     * `ahusection` / `ahucoil` / `ahufilter` but not `ahutunnels`. The reported
     * symptom was both visible halves of that gap at once: the AHU tab's
     * `Tunnel 1:1  __ W x __ H` selects kept the pre-APPLY numbers AND the 3D
     * cabinet kept the pre-APPLY geometry, until a full page reload.
     *
     * LEGACY REACHES THE SAME END STATE BY TWO SEPARATE ROADS, which is worth
     * knowing before "simplifying" this:
     *  - the SELECTS follow `syncdata_listener.js:204`'s generic
     *    `instance.set(message.data, {__from_back_end:true})` on the tunnel
     *    model, which `tab.ahu.tunnel.cabinet.js` re-renders from;
     *  - the CANVAS follows the HEADER, not the listener —
     *    `header.js:293-296` routes `model: 'ahutunnels'` to
     *    `forceUpdateTunnels(msg.id, dispatcher)`, whose first act is
     *    `dispatcher.trigger('change:tunnel:dimensions', {tunnel_id, render:true})`
     *    (`header.js:462-468`).
     * Here one cache write does both: `TunnelCabinetRow` renders the selects
     * straight off the `getAhuTunnelList` row, and the new row identity flows
     * into `canvasTunnels` → `canvasModelSignature` → `requestCanvasRedraw`
     * (narrowing 1 above — emitting `change:tunnel:dimensions` as well would
     * render the tunnel twice for one message).
     *
     * NOT ported, deliberately: `validateTunnel`. Legacy validates in the tunnel
     * SAVE callback (`model/tunnel.js:298-309`), which is the saving tab's path;
     * a tab receiving the echo does not re-validate, and doing it here would
     * fire board-wide validation writes nobody asked for.
     */
    const onPutTunnel = (msg: SocketDataMessage) => {
      const incoming = msg.data as unknown as IAhuBoardTunnel | undefined;
      const tunnelId = Number(incoming?.id ?? msg.id);
      const ahuId = ahuIdRef.current;
      if (!incoming || !Number.isFinite(tunnelId) || ahuId == null) return;

      // Scope: this board only — legacy's `stateStorage.models.get` miss.
      if (!tunnelIdsRef.current?.includes(tunnelId)) return;

      const cached = ahuBoardApi.endpoints.getAhuTunnelList
        .select(ahuId)(store.getState())
        .data?.find((row) => row.id === tunnelId);
      if (!cached) return;

      // Backbone's `changed` map — only keys whose value actually differs, so
      // the echo of the user's own W/H save mints no new array identity and the
      // canvas is not redrawn twice (narrowing 2).
      const changed: Record<string, unknown> = {};
      (Object.keys(incoming) as Array<keyof IAhuBoardTunnel>).forEach((key) => {
        if (incoming[key] !== cached[key]) changed[key as string] = incoming[key];
      });
      if (!Object.keys(changed).length) return;

      dispatch(
        ahuBoardApi.util.updateQueryData('getAhuTunnelList', ahuId, (rows) =>
          rows.map((row) => (row.id === tunnelId ? { ...row, ...changed } : row))
        )
      );

      // An X move renumbers the whole back-to-back group's `position` and the
      // echo only ever carries the one tunnel — same re-GET a locally-issued
      // PATCH does (refetchTunnelsAfterAddressingChange). Sits ABOVE the
      // width/height gate below: `x_offset` travels on its own.
      refetchTunnelsAfterAddressingChange(dispatch, changed, ahuId);

      // Same for the Bases checkboxes, twice over. They are computed from the
      // WHOLE unit's footprints, and the echo carries one tunnel — so another
      // browser's W/H, Y-Offset or airflow edit moves what EVERY tunnel here
      // builds. And the echo carries no `base_sides` at all (it is gated off
      // the write path, `AHUTunnelSerializer.get_base_sides`), so a plain
      // Bases save elsewhere needs its own re-read or this browser keeps the
      // pre-edit ticks — and, because `baseSidesPatchBody` drops a toggle equal to
      // the cached value, would silently swallow the user's attempt to put
      // the rail back.
      const isHybrid = Boolean(
        ahuBoardApi.endpoints.getAhuUnit.select(ahuId)(store.getState())?.data?.is_hybrid
      );
      // The echo of THIS browser's own save carries the same change the local
      // PATCH already re-read the list for, so skip the second trip. Another
      // user's echo is not claimed and still refreshes -- which is the whole
      // reason the echo path re-reads at all.
      const ownEcho = consumeOwnTunnelWrite(tunnelId);
      if (!ownEcho) {
        // `base_id` changing is its own case, and NOT hybrid-gated: the echo
        // is serialized without the read-path context, so it carries no
        // `builds_base` at all (`AHUTunnelSerializer.to_representation` pops
        // it). The key-by-key merge therefore leaves this board's cached
        // `false` sitting next to the new non-null `base_id` -- which locks
        // the Base select and draws the cabinet with no base until a reload.
        // Same re-read the local PATCH does through
        // `refetchTunnelsAfterBaseChange`; the sibling saves
        // `_resync_hybrid_base_ids` makes echo in the same shape -- which is
        // why it stands down while THIS browser's own base PATCH is still
        // propagating: that write re-reads the list once when it is done, and
        // every sibling echo in between would only queue a read of the same
        // rows behind it. Matched on the base too, so another user's change
        // to a DIFFERENT base on this unit still re-reads. A second board has
        // no local write, so this is the only thing that updates it.
        if (!isLocalBaseWriteInFlight(ahuId, changed.base_id)) {
          void refetchTunnelsAfterBaseChange(dispatch, store.getState, changed, ahuId);
        }
        refetchTunnelsAfterFootprintChange(
          dispatch,
          store.getState,
          changed,
          ahuId,
          isHybrid
        );
        refetchTunnelsAfterBaseSideChange(
          dispatch,
          store.getState,
          changed,
          ahuId,
          isHybrid
        );
      }

      // The siblings of `change:tunnel:dimensions` in the §19.2 tunnel cascade
      // (`TunnelCabinetRow.tsx:80-91`) — none of them has a cache cascade of its
      // own, so unlike the redraw they have to be emitted.
      if (!('width' in changed) && !('height' in changed)) return;
      // Same derived-dimension refresh a locally-issued W/H PATCH does — the
      // sizer tab's APPLY reaches this board only through this socket, and the
      // sections' aw_width/aw_height move with the tunnel without an
      // `ahusection` message of their own (refetchSectionsAfterTunnelResize).
      refetchSectionsAfterTunnelResize(dispatch, changed, [tunnelId]);
      ahuBoardBus.emit(`changed:tunnel_${tunnelId}:sizes`);
      if ('width' in changed) ahuBoardBus.emit(`changed:tunnel_${tunnelId}:width`);
      if ('height' in changed) ahuBoardBus.emit('get:overall:height', true);
      // Legacy armed ReCalculate here; the write that produced this message
      // re-laid the placement server-side, so re-read it instead (the same
      // thing a locally-issued W/H PATCH does — TunnelCabinetRow).
      void dispatch(
        refetchLugPlacementsAfterGeometryChange(ahuId, [tunnelId], {
          hideWhilePending: true,
        })
      );
    };

    const onData = (msg: SocketDataMessage) => {
      if (!msg || msg.type !== 'syncdata') return;
      if (msg.code === 'post' && ACCESSORY_DJANGO_MODELS.has(String(msg.model))) {
        onPostAccessory(msg);
        return;
      }
      if (msg.code === 'put' && msg.model === COIL_DJANGO_MODEL) {
        onPutCoil(msg);
        return;
      }
      if (msg.code === 'put' && msg.model === FILTER_DJANGO_MODEL) {
        onPutFilter(msg);
        return;
      }
      if (msg.code === 'put' && msg.model === TUNNEL_DJANGO_MODEL) {
        onPutTunnel(msg);
        return;
      }
      if (msg.model !== SECTION_DJANGO_MODEL || msg.code !== 'put') return;

      const incoming = msg.data as unknown as IAhuBoardSection | undefined;
      const sectionId = Number(incoming?.id ?? msg.id);
      const tunnelId = Number(incoming?.tunnel_id);
      if (!incoming || !Number.isFinite(sectionId) || !Number.isFinite(tunnelId)) return;

      // Scope: this board only. Backbone gets the same effect from
      // stateStorage — an unknown section id simply has no instance to set.
      if (!tunnelIdsRef.current?.includes(tunnelId)) return;

      // The user's own save is still on the wire; its own success handler
      // writes the authoritative row. Let it win (see narrowing 2).
      if (hasPendingSectionPatch(sectionId)) return;

      const cached = ahuBoardApi.endpoints.getTunnelSectionList
        .select(tunnelId)(store.getState())
        .data?.find((row) => row.id === sectionId);
      if (!cached) return;

      // Backbone's `changed` map: only keys whose value actually differs.
      const changed: Record<string, unknown> = {};
      (Object.keys(incoming) as Array<keyof IAhuBoardSection>).forEach((key) => {
        if (incoming[key] !== cached[key]) changed[key as string] = incoming[key];
      });
      if (!Object.keys(changed).length) return;

      dispatch(
        ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (rows) =>
          rows.map((row) => (row.id === sectionId ? { ...row, ...changed } : row))
        )
      );

      // tunnel.js:109-117 `recalc_length` — the tunnel's own length is kept in
      // lockstep with its sections CLIENT-SIDE (why: tunnelLength.ts). This is
      // the server-driven half of it; the client-driven half now rides the
      // section mutations themselves (ahuBoardFormsApi patch/create/delete),
      // because an optimistic write leaves `length` OUT of `changed` here.
      if ('length' in changed) {
        // ★ Deliberately ahead of (and wider than) the two-key gate below: a
        // length-only echo still has to move `tunnel.length`. The recalc inside
        // `followSectionLengthChange` is then a no-op — it returns early once
        // the sum already matches.
        recalcTunnelLength({ dispatch, getState: store.getState }, tunnelId);
      }

      // tunnel.js:121-127 — both dimension keys changed ⇒ the tunnel's geometry
      // moved. The canvas redraw rides the cache write (narrowing 1); the rest
      // is `followSectionLengthChange`, shared with the Checkpoint-4 cascade
      // refresh (which reaches this same state by re-reading rather than by an
      // echo). The GATE stays here and stays legacy's two-key one — see that
      // thunk's note on why the two callers gate differently.
      if (DIMENSION_FIELDS.every((field) => field in changed)) {
        void dispatch(followSectionLengthChange(ahuIdRef.current, tunnelId));
      }
    };

    socket.on('data', onData);
    return () => {
      socket.off('data', onData);
      accessoryTimers.forEach((t) => window.clearTimeout(t));
      accessoryTimers.clear();
      coilTimers.forEach((t) => window.clearTimeout(t));
      coilTimers.clear();
    };
  }, [dispatch]);
}
